import { memo, useMemo } from 'react';
import type { ComponentType } from 'react';
import { useTranslate } from '@tolgee/react';
import { useRequest } from 'ahooks';
import dayjs from 'lib/dayjs';
import { toNumber } from 'lodash-es';
import useSystem from 'hooks/business/system/useSystem';
import { overseaTranslate } from 'services/api/overseaTranslate';
import { formatNumber } from 'utils/numberFormat';
import type { TradeDetailAmountBlock } from 'views/Conversation/Sider/tradeDetailBlockTypes';
import { PaymentItem } from './PaymentItem';

interface PaymentRow {
  key: string;
  label: string;
  price: number;
  description?: string | React.ReactNode;
  size?: 'small' | 'medium';
}

interface PaymentRowConfig {
  key: string;
  label: string;
  value: unknown;
  description?: string | React.ReactNode;
  size?: PaymentRow['size'];
}

interface PaymentBlockProps {
  order: Conversation.Trade.Vo;
  showSmall?: boolean;
}

const hasAmount = (value: unknown): value is string | number => {
  if (typeof value === 'number') {
    return value === 0 || Boolean(value);
  }

  return typeof value === 'string' && value !== '';
};

const createPaymentRows = (configs: PaymentRowConfig[]) =>
  configs.reduce<PaymentRow[]>((rows, { key, label, value, description, size = 'medium' }) => {
    if (!hasAmount(value)) {
      return rows;
    }

    rows.push({
      key,
      label,
      price: toNumber(value),
      description,
      size,
    });

    return rows;
  }, []);

const PaymentRows = memo(
  ({ rows, unit, showSmall }: { rows: PaymentRow[]; unit?: string; showSmall?: boolean }) => {
    const visibleRows = showSmall ? rows : rows.filter((row) => row.size !== 'small');

    if (visibleRows.length === 0) {
      return null;
    }

    return (
      <>
        {visibleRows.map((item) => (
          <PaymentItem
            key={item.key}
            label={item.label}
            price={item.price}
            description={item.description}
            size={item.size}
            unit={unit}
          />
        ))}
      </>
    );
  },
);

const formatAdjustmentLabel = (
  adjustmentReason: string,
  adjustmentTimeMs?: string | number,
  translate?: string,
) => {
  const validTime = !!adjustmentTimeMs && adjustmentTimeMs !== '0';
  const dateLabel = validTime ? `${dayjs(toNumber(adjustmentTimeMs)).format('YYYY-MM-DD')} ` : '';

  return `${dateLabel}${translate || adjustmentReason}`;
};

const useShopeeAdjustmentLabels = (tradeAdjustment: Conversation.Trade.TradeAdjustment[] = []) => {
  const {
    systemSetting: { language: sysLang },
  } = useSystem();

  const defaultLabels = useMemo(
    () =>
      tradeAdjustment.map(({ adjustmentReason, adjustmentTimeMs }) =>
        formatAdjustmentLabel(adjustmentReason, adjustmentTimeMs),
      ),
    [tradeAdjustment],
  );

  const { data } = useRequest(
    async () => {
      const texts = tradeAdjustment.map(({ adjustmentReason }) => adjustmentReason);
      const translates = await overseaTranslate(texts, sysLang, 'side');

      return tradeAdjustment.map(({ adjustmentReason, adjustmentTimeMs }, index) =>
        formatAdjustmentLabel(adjustmentReason, adjustmentTimeMs, translates[index]),
      );
    },
    {
      ready: tradeAdjustment.length > 0,
    },
  );

  return data || defaultLabels;
};

const formatPriceDescription = (unit: string | undefined, value: unknown) => {
  if (!hasAmount(value)) {
    return '';
  }

  return `${unit} ${formatNumber(toNumber(value), '0,0.00')}`;
};

const buildPaymentLazadaRows = (
  order: Conversation.Trade.Vo,
  t: ReturnType<typeof useTranslate>['t'],
) =>
  createPaymentRows([
    { key: 'payment_lazada_item_price', label: t('trade.商品小计'), value: order.itemPrice },
    {
      key: 'payment_lazada_shipping_fee_actual',
      label: t('trade.运输费用'),
      value: order.shippingFeeActual,
      description: (
        <>
          <div>
            {hasAmount(order.shippingFeeOriginal)
              ? `${t('trade.原始费用')}: ${formatPriceDescription(
                  order.unit,
                  order.shippingFeeOriginal,
                )}`
              : undefined}
          </div>
          <div>
            {hasAmount(order.shippingFeeDiscount)
              ? `${t('trade.运费折扣')}: ${formatPriceDescription(
                  order.unit,
                  order.shippingFeeDiscount,
                )}`
              : undefined}
          </div>
        </>
      ),
    },
    {
      key: 'payment_lazada_shipping_fee_original',
      label: t('trade.原始费用'),
      value: order.shippingFeeOriginal,
      size: 'small',
    },
    {
      key: 'payment_lazada_shipping_fee_discount',
      label: t('trade.运费折扣'),
      value: order.shippingFeeDiscount,
      size: 'small',
    },
    {
      key: 'payment_lazada_seller_promotion',
      label: t('trade.商家优惠券'),
      value: order.sellerPromotion,
    },
    {
      key: 'payment_lazada_total_amount',
      label: t('trade.买家付款总额'),
      value: order.totalAmount,
    },
    {
      key: 'payment_lazada_voucher_platform',
      label: t('trade.Lazada总折扣'),
      value: order.voucherPlatform,
    },
    {
      key: 'payment_lazada_voucher_seller',
      label: t('trade.卖家折扣总额'),
      value: order.voucherSeller,
    },
    { key: 'payment_lazada_adjust_amount', label: t('trade.调整金额'), value: order.adjustAmount },
  ]);

const buildPaymentTikTokRows = (
  order: Conversation.Trade.Vo,
  t: ReturnType<typeof useTranslate>['t'],
) => {
  const buyerPayment = order.buyerPayment;

  if (!hasAmount(buyerPayment?.buyerTotalAmount)) {
    return [];
  }

  return createPaymentRows([
    {
      key: 'payment_tiktok_item_price_after_discount',
      label: t('tiktokPayment.商品小计'),
      value: buyerPayment?.itemPriceAfterDiscount,
    },
    {
      key: 'payment_tiktok_item_price',
      label: t('tiktokPayment.商品总价'),
      value: buyerPayment?.itemPrice,
      size: 'small',
    },
    {
      key: 'payment_tiktok_voucher_from_seller',
      label: t('tiktokPayment.店铺折扣'),
      value: buyerPayment?.voucherFromSeller,
      size: 'small',
    },
    {
      key: 'payment_tiktok_voucher_from_platform',
      label: t('tiktokPayment.平台折扣'),
      value: buyerPayment?.voucherFromPlatform,
      size: 'small',
    },
    {
      key: 'payment_tiktok_shipping_fee',
      label: t('tiktokPayment.运费小计'),
      value: buyerPayment?.buyerPaidShippingFee,
      description: t('tiktokPayment.运费小计tips'),
    },
    {
      key: 'payment_tiktok_original_shipping_fee',
      label: t('tiktokPayment.原始运费'),
      value: buyerPayment?.originalShippingFee,
      size: 'small',
    },
    {
      key: 'payment_tiktok_shipping_fee_seller_discount',
      label: t('tiktokPayment.店铺运费折扣'),
      value: buyerPayment?.shippingFeeSellerDiscount,
      size: 'small',
    },
    {
      key: 'payment_tiktok_shipping_fee_platform_discount',
      label: t('tiktokPayment.平台运费折扣'),
      value: buyerPayment?.shippingFeePlatformDiscount,
      size: 'small',
    },
    { key: 'payment_tiktok_taxes', label: t('tiktokPayment.税费等'), value: buyerPayment?.taxes },
    {
      key: 'payment_tiktok_small_order_fee',
      label: t('tiktokPayment.最低消费差额'),
      value: buyerPayment?.smallOrderFee,
      description: t('tiktokPayment.最低消费差额tips'),
    },
    {
      key: 'payment_tiktok_total_amount',
      label: t('tiktokPayment.付款总金额'),
      value: buyerPayment?.buyerTotalAmount,
      description: t('tiktokPayment.付款总金额tips'),
    },
  ]);
};

const buildIncomeLazadaRows = (
  order: Conversation.Trade.Vo,
  t: ReturnType<typeof useTranslate>['t'],
) => {
  const customTradeIncome = order.customTradeIncome || [];
  const index = customTradeIncome.findIndex((item) => item.name === 'Grand Total');
  const grandTotal = customTradeIncome[index];
  const lastCustomTradeIncome = [...customTradeIncome];

  if (index !== -1) {
    lastCustomTradeIncome.splice(index, 1);
  }

  return createPaymentRows([
    ...lastCustomTradeIncome.map((item, idx) => ({
      key: `income_lazada_detail_${idx}_${item.name}`,
      label: t({
        key: `lazadaIncome.${item.name}`,
        defaultValue: item.name,
      }),
      value: item.price,
      size: 'small' as const,
    })),
    {
      key: 'income_lazada_grand_total',
      label: t('lazadaIncome.Grand Total'),
      value: grandTotal?.price ?? 0,
    },
  ]);
};

const buildIncomeKwaiRows = (
  order: Conversation.Trade.Vo,
  t: ReturnType<typeof useTranslate>['t'],
) => {
  if (!hasAmount(order.buyerPayment?.buyerTotalAmount)) {
    return [];
  }

  return createPaymentRows([
    {
      key: 'income_kwai_total',
      label: t('trade.买家实付金额'),
      value: order.buyerPayment?.buyerTotalAmount,
    },
  ]);
};

const buildIncomeMercadoRows = (
  order: Conversation.Trade.Vo,
  t: ReturnType<typeof useTranslate>['t'],
) => {
  const tradeIncome = order.tradeIncome;

  if (!hasAmount(tradeIncome?.escrowAmount)) {
    return [];
  }

  return createPaymentRows([
    {
      key: 'income_mercado_order_amount_parent',
      label: t('销售金额(收入)'),
      value: tradeIncome?.orderAmount,
    },
    {
      key: 'income_mercado_order_amount',
      label: t('销售额'),
      value: tradeIncome?.orderAmount,
      size: 'small',
    },
    {
      key: 'income_mercado_cancel_price',
      label: t('平台佣金(支出)'),
      value: tradeIncome?.cancelPrice,
      size: 'small',
    },
    {
      key: 'income_mercado_refund_item_price',
      label: t('部分退款'),
      value: tradeIncome?.refundItemPrice,
      size: 'small',
    },
    {
      key: 'income_mercado_commission_fee_parent',
      label: t('平台佣金(支出)'),
      value: tradeIncome?.commissionFee,
    },
    {
      key: 'income_mercado_commission_fee',
      label: t('平台佣金(支出)'),
      value: tradeIncome?.commissionFee,
      size: 'small',
    },
    {
      key: 'income_mercado_commission_fee_return',
      label: t('平台退回佣金'),
      value: tradeIncome?.commissionFeeReturn,
      size: 'small',
    },
    {
      key: 'income_mercado_shipping_fee',
      label: t('运费小计(实际)'),
      value: tradeIncome?.shippingFee,
    },
  ]);
};

const buildIncomeShopeeRows = (
  order: Conversation.Trade.Vo,
  t: ReturnType<typeof useTranslate>['t'],
  adjustmentLabels: string[],
) => {
  const { buyerPayment, tradeIncome } = order;
  const rows: PaymentRowConfig[] = [];

  if (hasAmount(buyerPayment?.buyerTotalAmount)) {
    rows.push(
      { key: 'income_shopee_item_price', label: t('商品金额'), value: buyerPayment?.itemPrice },
      {
        key: 'income_shopee_product_protection',
        label: t('商品险'),
        value: buyerPayment?.productProtection,
      },
      {
        key: 'income_shopee_shipping_fee',
        label: t('trade.运费'),
        value: buyerPayment?.buyerPaidShippingFee,
      },
      {
        key: 'income_shopee_shipping_fee_sst',
        label: t('买家支付运费SST'),
        value: buyerPayment?.shippingFeeSst,
      },
      {
        key: 'income_shopee_voucher_from_platform',
        label: t('Shopee商品折扣补贴'),
        value: buyerPayment?.voucherFromPlatform,
      },
      {
        key: 'income_shopee_voucher_from_seller',
        label: t('trade.卖家券'),
        value: buyerPayment?.voucherFromSeller,
      },
      { key: 'income_shopee_coins', label: t('trade.Shopee币折抵'), value: buyerPayment?.coins },
      {
        key: 'income_shopee_credit_card_promotion',
        label: t('trade.信用卡折扣'),
        value: buyerPayment?.creditCardPromotion,
      },
      {
        key: 'income_shopee_cross_border_tax',
        label: t('trade.进口税'),
        value: buyerPayment?.crossBorderTax,
      },
      { key: 'income_shopee_icms_tax', label: t('ICMS'), value: buyerPayment?.icmsTax },
      { key: 'income_shopee_other_taxes', label: t('其他税费'), value: buyerPayment?.otherTaxes },
      { key: 'income_shopee_service_fee', label: t('服务费'), value: buyerPayment?.serviceFee },
      {
        key: 'income_shopee_buyer_transaction_fee',
        label: t('trade.买家交易费用'),
        value: buyerPayment?.buyerTransactionFee,
      },
      {
        key: 'income_shopee_buyer_total_amount',
        label: t('trade.买家实付金额'),
        value: buyerPayment?.buyerTotalAmount,
      },
    );
  }

  const totalIncome = tradeIncome?.finalAmount || tradeIncome?.escrowAmount;
  if (!hasAmount(totalIncome)) {
    return createPaymentRows(rows);
  }

  rows.push(
    { key: 'income_shopee_trade_item_price', label: t('商品金额'), value: tradeIncome?.itemPrice },
    {
      key: 'income_shopee_trade_origin_item_price',
      label: t('trade.商品价格'),
      value: tradeIncome?.originItemPrice,
      size: 'small',
    },
    {
      key: 'income_shopee_trade_refund_item_price',
      label: t('trade.退款金额'),
      value: tradeIncome?.refundItemPrice,
      size: 'small',
    },
    {
      key: 'income_shopee_trade_shipping_fee',
      label: t('运费小计(实际)'),
      value: tradeIncome?.shippingFee,
    },
    {
      key: 'income_shopee_trade_buyer_paid_shipping_fee',
      label: t('买家支付运费'),
      value: tradeIncome?.buyerPaidShippingFee,
      size: 'small',
    },
    {
      key: 'income_shopee_trade_actual_shipping_fee',
      label: t('物流供应商收取的运费'),
      value: tradeIncome?.actualShippingFee,
      size: 'small',
    },
    {
      key: 'income_shopee_trade_refund_shipping_fee',
      label: t('trade.退款运费'),
      value: tradeIncome?.refundShippingFee,
      size: 'small',
    },
    {
      key: 'income_shopee_trade_shopee_shipping_rebate',
      label: t('Shopee运费补贴'),
      value: tradeIncome?.shopeeShippingRebate,
      size: 'small',
    },
    {
      key: 'income_shopee_trade_shipping_fee_discount_from_3pl',
      label: t('trade.运费折扣'),
      value: tradeIncome?.shippingFeeDiscountFrom3pl,
      size: 'small',
    },
    {
      key: 'income_shopee_trade_discount_fee',
      label: t('优惠券和补贴'),
      value: tradeIncome?.discountFee,
    },
    {
      key: 'income_shopee_trade_voucher_from_seller',
      label: t('trade.卖家券'),
      value: tradeIncome?.voucherFromSeller,
      size: 'small',
    },
    {
      key: 'income_shopee_trade_shopee_discount',
      label: t('Shopee商品折扣补贴'),
      value: tradeIncome?.shopeeDiscount,
      size: 'small',
    },
    {
      key: 'income_shopee_trade_charges_fee',
      label: t('trade.费用与收费'),
      value: tradeIncome?.chargesFee,
    },
    {
      key: 'income_shopee_trade_commission_fee',
      label: t('佣金'),
      value: tradeIncome?.commissionFee,
      size: 'small',
    },
    {
      key: 'income_shopee_trade_service_fee',
      label: t('服务费'),
      value: tradeIncome?.serviceFee,
      size: 'small',
    },
    {
      key: 'income_shopee_trade_seller_transaction_fee',
      label: t('trade.交易手续费'),
      value: tradeIncome?.sellerTransactionFee,
      size: 'small',
    },
    {
      key: 'income_shopee_trade_vat',
      label: t('增值税'),
      value: tradeIncome?.vat,
      size: 'small',
    },
    {
      key: 'income_shopee_trade_crossBorderTax',
      label: t('进口关税'),
      value: tradeIncome?.crossBorderTax,
      size: 'small',
    },
  );

  if (['MY', 'SG'].includes(order.region || '')) {
    rows.push({
      key: 'income_shopee_trade_sales_tax_on_lvg',
      label: t('sales_tax_on_lvg'),
      value: tradeIncome?.salesTaxOnLvg,
      size: 'small',
    });
  }

  rows.push({
    key: 'income_shopee_trade_escrow_amount',
    label: t('订单应收款'),
    value: tradeIncome?.escrowAmount,
  });

  rows.push(
    ...adjustmentLabels.map((label, index) => ({
      key: `income_shopee_trade_adjustment_${index}`,
      label,
      value: tradeIncome?.tradeAdjustment?.[index]?.amount,
      size: 'small' as const,
    })),
    {
      key: 'income_shopee_trade_total_adjustment',
      label: t('调整总额'),
      value: tradeIncome?.totalAdjustment,
    },
    {
      key: 'income_shopee_trade_final_amount',
      label: t('最终值'),
      value: tradeIncome?.finalAmount,
    },
  );

  return createPaymentRows(rows);
};

const PaymentLazadaBlock = memo(({ order, showSmall }: PaymentBlockProps) => {
  const { t } = useTranslate();
  const rows = useMemo(() => buildPaymentLazadaRows(order, t), [order, t]);

  return <PaymentRows rows={rows} unit={order.unit} showSmall={showSmall} />;
});

const PaymentTikTokBlock = memo(({ order, showSmall }: PaymentBlockProps) => {
  const { t } = useTranslate();
  const rows = useMemo(() => buildPaymentTikTokRows(order, t), [order, t]);

  return <PaymentRows rows={rows} unit={order.unit} showSmall={showSmall} />;
});

const IncomeLazadaBlock = memo(({ order, showSmall }: PaymentBlockProps) => {
  const { t } = useTranslate();
  const rows = useMemo(() => buildIncomeLazadaRows(order, t), [order, t]);

  return <PaymentRows rows={rows} unit={order.unit} showSmall={showSmall} />;
});

const IncomeKwaiBlock = memo(({ order, showSmall }: PaymentBlockProps) => {
  const { t } = useTranslate();
  const rows = useMemo(() => buildIncomeKwaiRows(order, t), [order, t]);

  return <PaymentRows rows={rows} unit={order.unit} showSmall={showSmall} />;
});

const IncomeShopeeBlock = memo(({ order, showSmall }: PaymentBlockProps) => {
  const { t } = useTranslate();
  const adjustmentLabels = useShopeeAdjustmentLabels(order.tradeIncome?.tradeAdjustment);
  const rows = useMemo(
    () => buildIncomeShopeeRows(order, t, adjustmentLabels),
    [adjustmentLabels, order, t],
  );

  return <PaymentRows rows={rows} unit={order.unit} showSmall={showSmall} />;
});

const IncomeMercadoBlock = memo(({ order, showSmall }: PaymentBlockProps) => {
  const { t } = useTranslate();
  const rows = useMemo(() => buildIncomeMercadoRows(order, t), [order, t]);

  return <PaymentRows rows={rows} unit={order.unit} showSmall={showSmall} />;
});

const amountBlockComponentMap: Record<
  TradeDetailAmountBlock['variant'],
  ComponentType<PaymentBlockProps>
> = {
  payment_lazada: PaymentLazadaBlock,
  payment_tiktok: PaymentTikTokBlock,
  income_lazada: IncomeLazadaBlock,
  income_kwai: IncomeKwaiBlock,
  income_shopee: IncomeShopeeBlock,
  income_mercado: IncomeMercadoBlock,
};

export const PaymentAmountBlockContent = ({
  block,
  order,
  showSmall,
}: {
  block: TradeDetailAmountBlock;
  order: Conversation.Trade.Vo;
  showSmall?: boolean;
}) => {
  const Component = amountBlockComponentMap[block.variant];

  return <Component order={order} showSmall={showSmall} />;
};
